import React, { useRef, useState } from "react";
import { Box, Collapse } from "@mui/material";
import dayjs from "dayjs";
import FilterBoxschema from "@/Config/ImportJob/Dashboard/FilterBox";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";

export default function FilterBar({
  onSearch,
  setIsFilterApplied,
  setFilteredData,
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filledData, setFilledData] = useState({});
  const [formKey, setFormKey] = useState(0);
  const [filterButtonLabel, setFilterButtonLabel] =
    useState("Advanced Filters");
  const formRef = useRef();
  const advRef1 = useRef();
  const advRef2 = useRef();
  const advRef3 = useRef();

  // ✅ Reset Filters
  const handleReset = async () => {
    await Promise.all([
      formRef.current?.reset?.(),
      advRef1.current?.reset?.(),
      advRef2.current?.reset?.(),
      advRef3.current?.reset?.(),
    ]);
    setFilledData({});
    setIsFilterApplied?.(false);
    onSearch(null);
    setShowAdvanced(false); // ✅ collapse after reset
    setFilterButtonLabel("Advanced Filters"); // ✅ reset label
    setFormKey((prev) => prev + 1);
  };

  // ✅ Live CommonSearch Handler (with detailed logs)
  const handleCommonSearch = (name, value) => {
    if (name === "commonSearch") {
      let searchText = "";

      if (value?.target?.value !== undefined) {
        searchText = value.target.value.trim();
      } else if (typeof value === "string") {
        searchText = value.trim();
      } else if (value?.label) {
        searchText = value.label.trim();
      } else if (value?.value) {
        searchText = String(value.value).trim();
      }

      setFilledData((prev) => ({
        ...prev,
        commonSearch: searchText,
      }));

      if (searchText.length === 0) {
        if (typeof setFilteredData === "function") setFilteredData(null);
        setIsFilterApplied?.(false);
        onSearch?.({ filter: {} });
      } else if (searchText.length >= 3) {
        onSearch?.({
          filter: { commonSearch: searchText },
        });
      } else {
        console.log("⏳ Waiting... (less than 3 characters typed)");
      }
    }
  };

  // ✅ Toggle advanced filters
  const handleToggleAdvanced = () => {
    if (filterButtonLabel === "Clear Filters") {
      // 🔹 if button says Clear Filters → just reset
      handleReset();
    } else {
      // 🔹 otherwise open advanced filters
      setShowAdvanced(true);
    }
  };

  // ✅ Full Search Handler
  const handleSearchClick = async () => {
    const mainData =
      (formRef.current && (await formRef.current.submit())) || {};
    const adv1 = (advRef1.current && (await advRef1.current.submit())) || {};
    const adv2 = (advRef2.current && (await advRef2.current.submit())) || {};
    const adv3 = (advRef3.current && (await advRef3.current.submit())) || {};

    const combinedData = {
      ...mainData,
      ...adv1,
      ...adv2,
      ...adv3,
      ...filledData,
    };
    setFilledData(combinedData);

    //   const jobNo =
    //   combinedData?.task === "jobNo" ? combinedData?.jobNumber : undefined;
    // const invoiceNo =
    //   combinedData?.task === "invoiceNo" ? combinedData?.jobNumber : undefined;
    // const containerNo =
    //   combinedData?.task === "containerNo" ? combinedData?.jobNumber : undefined;
    //   console.log("jobNojobNo",jobNo)
    //   console.log("invoiceNo",invoiceNo)
    //   console.log("containerNo",containerNo)
    const keyMap = {
      jobNo: "jobNo",
      invoiceNo: "invoiceNo",
      containerNo: "containerNo",
    };

    const selectedKey = keyMap[combinedData?.task];
    const dynamicFilter =
      selectedKey && combinedData?.jobNo
        ? { [selectedKey]: combinedData.jobNo }
        : {};

    const payload = {
      filter: {
        ...dynamicFilter,
        commonSearch: combinedData?.commonSearch || "",
        portOfShipmentId: combinedData?.portOfShipment?.id || "",
        customHouse: combinedData?.customHouse || "",
        mode: combinedData?.TransportMode || "",
        beType: combinedData?.beType || "",
        importerId:
          combinedData?.importerName?.id || combinedData?.importerId || "",
        status: combinedData?.status || "",
        dateRange:
          combinedData?.dateRange?.[0] && combinedData?.dateRange?.[1]
            ? {
                from: dayjs(combinedData.dateRange[0])
                  .startOf("day")
                  .toISOString(),
                to: dayjs(combinedData.dateRange[1]).endOf("day").toISOString(),
              }
            : undefined,
        active: combinedData?.Active || "",
        pageNumber: combinedData?.pageNumber || 1,
        pageSize: combinedData?.pageSize || 20,
      },
    };
    onSearch(payload);
    setIsFilterApplied?.(true);

    // ✅ collapse after search and update label
    setShowAdvanced(false);
    setFilterButtonLabel("Clear Filters");
  };

  const updatedSchema = {
    ...FilterBoxschema,
    row1: FilterBoxschema.row1.map((col) => ({
      ...col,
      rows: {
        row1: col.rows?.row1?.map((field) =>
          field.name === "Advanced Filters"
            ? { ...field, label: filterButtonLabel }
            : field
        ),
      },
    })),
  };

  // ✅ Render
  return (
    <Box
      sx={{
        backgroundColor: "var(--gray-0)",
        border: "1px solid var(--brand-200)",
        borderRadius: 2,
        py: 1,
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSearchClick();
        }
      }}
    >
      {/* 🔹 Main Filter */}
      <FormGenerator
        ref={formRef}
        showLoader={false}
        schema={updatedSchema}
        onChange={handleCommonSearch}
        onSubmit={({ action, values }) => {
          if (values) setFilledData(values);
          if (action === "toggleAdvanced") handleToggleAdvanced();
          if (action === "search") handleSearchClick();
          if (action === "closeAdvanced") handleReset();
        }}
      />

      {/* 🔹 Advanced Filters */}
      <Collapse in={showAdvanced}>
        <Box>
          <Box sx={{ px: 2 }}>
            <FormGenerator
              showLoader={false}
              ref={advRef1}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: {
                    row2: col.rows?.row2 || [],
                    row3: col.rows?.row3 || [],
                    row4: col.rows?.row4 || [],
                  },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>
          <Box>
            <FormGenerator
              showLoader={false}
              ref={advRef2}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: { row5: col.rows?.row5 || [] },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>

          <Box sx={{ px: 2 }}>
            <FormGenerator
              showLoader={false}
              ref={advRef3}
              schema={{
                row1: FilterBoxschema.row1.map((col) => ({
                  ...col,
                  rows: { row6: col.rows?.row6 || [] },
                })),
              }}
              onChange={handleCommonSearch}
              onSubmit={({ action }) => {
                if (action === "toggleAdvanced") handleToggleAdvanced();
                if (action === "search") handleSearchClick();
                if (action === "closeAdvanced") handleReset();
              }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
